import React, { useState, useEffect, useCallback } from 'react';
import { fetchFeed } from '../api/rss';
import Header from '../components/ui/Header';
import StandardCard from '../components/ui/StandardCard';
import Loader from '../components/ui/Loader';
import SEOHead from '../components/ui/SEOHead';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { motion } from 'framer-motion';

const MAIN_CATEGORIES = [
  { id: 'All', label: 'Latest', type: 'feed', url: 'https://sulit.ph/feed/' },
  { id: 'Buys', label: 'Buys', type: 'feed', url: 'https://sulit.ph/category/sulit-buys/feed/' },
  { id: 'Living', label: 'Living', type: 'feed', url: 'https://sulit.ph/category/sulit-living/feed/' },
  { id: 'Decisions', label: 'Decisions', type: 'feed', url: 'https://sulit.ph/category/sulit-decisions/feed/' },
  { id: 'Experiences', label: 'Experiences', type: 'feed', url: 'https://sulit.ph/category/sulit-experiences/feed/' },
  { id: 'About', label: 'About', type: 'link', url: 'https://sulit.ph/about-sulit-ph/' }
];

const Home = () => {
  const [articles, setArticles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [activeCategory, setActiveCategory] = useState(MAIN_CATEGORIES[0]);

  const loadFeed = useCallback(async (category, isRefresh = false) => {
    if (category.type === 'link') return;

    if (isRefresh) {
      setIsRefreshing(true);
    } else {
      setIsLoading(true);
    }
    setError(null);

    try {
      const feedData = await fetchFeed(category.url);
      if (feedData.length > 0) {
        setArticles(feedData);
      } else {
        setError(`No articles found for ${category.label}.`);
        setArticles([]);
      }
    } catch (err) {
      setError('Failed to load the magazine. Please try again later.');
      setArticles([]);
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadFeed(activeCategory);
  }, [activeCategory, loadFeed]);

  const handleCategoryClick = (cat) => {
    if (cat.type === 'link') {
      window.open(cat.url, '_blank', 'noopener,noreferrer');
    } else {
      setActiveCategory(cat);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 font-sans text-gray-900 pb-10 selection:bg-black selection:text-white">
      <SEOHead activeCategory={activeCategory} articles={articles} />
      
      <Header onRefresh={() => loadFeed(activeCategory, true)} isRefreshing={isRefreshing} />

      <main className="max-w-4xl mx-auto">
        <motion.div 
          initial={{ opacity: 0, y: 10 }} 
          animate={{ opacity: 1, y: 0 }} 
          transition={{ duration: 0.5 }}
          className="w-full px-4 pt-6 mb-6"
        >
          <img 
            src="https://media-manager-c.questera.ai/greta-media/29a20af30bd50f0331d2a32e8bab2d926b9f480b7a7f7fa86d6a5fc65541d424101393f9afc6e1f60f8b008209294a43/images/aW1hZ2UvcG5n/95ece4096ace2a6ec057e1f60d6111b0.png" 
            alt="Sulit.ph Banner" 
            width="896"
            height="300"
            fetchPriority="high"
            className="w-full h-auto rounded-2xl shadow-sm object-cover bg-white aspect-[3/1]"
          />
        </motion.div>

        <nav className="px-4 mb-6 overflow-x-auto scrollbar-hide sticky top-[72px] z-40 bg-gray-50 py-2 -mx-4 sm:mx-0 sm:px-0" aria-label="Category Navigation">
          <div className="flex gap-2 min-w-max px-4 sm:px-0">
            {MAIN_CATEGORIES.map((cat) => (
              <button
                key={cat.id}
                onClick={() => handleCategoryClick(cat)}
                className={`px-5 py-2 flex justify-center items-center gap-1.5 rounded-xl text-sm font-bold transition-all ${
                  activeCategory.id === cat.id && cat.type !== 'link'
                    ? 'bg-orange-500 text-white shadow-md'
                    : 'bg-white text-orange-600 hover:bg-orange-50 border border-orange-100 shadow-sm'
                }`}
                aria-current={activeCategory.id === cat.id ? 'page' : undefined}
              >
                {cat.label}
                {cat.type === 'link' && <SafeIcon icon={FiIcons.FiExternalLink} className="text-sm opacity-80" aria-hidden="true" />}
              </button>
            ))}
          </div>
        </nav>

        {isLoading ? (
          <Loader />
        ) : error ? (
          <div className="flex flex-col items-center justify-center min-h-[40vh] px-4 text-center">
            <SafeIcon icon={FiIcons.FiAlertCircle} className="text-4xl text-gray-400 mb-4" />
            <p className="text-gray-600 mb-4">{error}</p>
            <button
              onClick={() => loadFeed(activeCategory)}
              className="px-6 py-2 bg-black text-white rounded-full font-medium hover:bg-gray-800 transition-colors"
            >
              Try Again
            </button>
          </div>
        ) : (
          <section className="px-4 mb-6">
            <h2 className="text-sm font-bold uppercase tracking-widest text-gray-500 border-b border-gray-200 pb-2 mb-6">
              {activeCategory.id === 'All' ? 'Latest Stories' : `Browsing: ${activeCategory.label}`}
            </h2>
            
            {articles.length > 0 ? (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {articles.map((article, index) => (
                    <StandardCard key={article.id} article={article} index={index} />
                  ))}
                </div>
                
                <div className="text-center py-10 mt-4">
                  <div className="inline-block w-12 h-1 bg-gray-200 rounded-full mb-4"></div>
                  <p className="text-gray-400 font-serif italic">You're all caught up</p>
                </div>
              </>
            ) : (
              <div className="text-center py-20 px-4">
                <SafeIcon icon={FiIcons.FiInbox} className="text-4xl text-gray-300 mb-3 mx-auto" />
                <p className="text-gray-500 font-medium">No articles found for "{activeCategory.label}".</p>
                <button
                  onClick={() => handleCategoryClick(MAIN_CATEGORIES[0])}
                  className="mt-4 text-blue-600 text-sm font-bold hover:underline"
                >
                  View latest articles
                </button>
              </div>
            )}
          </section>
        )}
      </main>
    </div>
  );
};

export default Home;